import removeIcon from "assets/images/remove-icon.svg";
import {parse} from "date-fns";
import {useState} from "react";
import {
  common as stringsCommon,
  medicationRefillUnauth as strings,
} from "strings";
import {v4 as uuidv4} from "uuid";

import Button from "components/Button";
import ConfirmRefillRequest from "components/ConfirmRefillRequest";
import {DateString} from "components/DateInput";
import PageTitle from "components/PageTitle";
import RefillPlacedModal from "components/RefillPlacedModal";
import StyledDateInput from "components/StyledDateInput";
import TextInput from "components/TextInput";

import * as models from "liberty-core/api/models";
import {useRefillRequestUnregistered} from "liberty-core/api/react-query/refillRequest";

import {useAlert} from "lib/alert";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./MedicationRefillUnauthPage.module.css";

function AdditionalRxField(props: {
  value: string;
  errorText: string;
  setValue: (value: string) => void;
  onRemove: () => void;
}) {
  return (
    <div className={styles.additionalRxFieldContainer}>
      <TextInput
        className={styles.formInputField}
        label={stringsCommon.rxNumber}
        type="text"
        value={props.value}
        errorText={props.errorText}
        onChangeText={props.setValue}
      />
      <button onClick={props.onRemove} className={styles.removeButton}>
        <img
          className={styles.removeIcon}
          src={removeIcon}
          alt={strings.removeAlt}
        />
      </button>
    </div>
  );
}

function RegisterSidebar() {
  return (
    <div className={styles.registerContainer}>
      <div className={styles.registerDescriptionText}>
        {strings.registerPrompt}
      </div>
      <Button
        className={styles.registerButton}
        variant="unfilled"
        to="/register"
      >
        {stringsCommon.register}
      </Button>
      <span className={styles.rx365text}>{stringsCommon.rx365}</span>
    </div>
  );
}

const isNotNumeric = (val: string) => {
  const notNumericPattern = /[^0-9]/g;

  return val.match(notNumericPattern);
};

export default function MedicationRefillUnauthPage() {
  const [pharmacy] = useCurrentPharmacy();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<DateString>("");
  const [rxNumbers, setRxNumbers] = useState<
    Array<{
      value: string;
      id: string;
      errorText: string;
    }>
  >([{value: "", id: uuidv4(), errorText: ""}]);
  const [showConfirmRefillRequest, setShowConfirmRefillRequest] =
    useState(false);
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<models.TimeString>();
  const [comment, setComment] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [response, setResponse] = useState<models.RefillResponse[]>([]);

  const {mutateAsync: submitRefillRequest, isLoading: isLoading} =
    useRefillRequestUnregistered();
  const [submitting, setSubmitting] = useState(false);

  const alert = useAlert();

  return (
    <div className={styles.bigDiv}>
      <PageTitle className={styles.pageTitle}>{strings.title}</PageTitle>
      <div className={styles.flexContainer}>
        <div className={styles.formContainer}>
          <TextInput
            className={styles.formInputField}
            label={stringsCommon.firstName}
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChangeText={setFirstName}
          />
          <TextInput
            className={styles.formInputField}
            label={stringsCommon.lastName}
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChangeText={setLastName}
          />
          <StyledDateInput
            className={styles.formInputField}
            label={stringsCommon.dateOfBirth}
            autoComplete="bday"
            value={dateOfBirth}
            onChangeDate={setDateOfBirth}
          />
          <TextInput
            className={styles.formInputField}
            label={stringsCommon.rxNumber}
            type="text"
            value={rxNumbers[0].value}
            errorText={rxNumbers[0].errorText}
            onChangeText={(text) => {
              const errorText = isNotNumeric(text)
                ? strings.rxNumberNotANumber
                : "";
              setRxNumbers((prev) => [
                {value: text, id: prev[0].id, errorText},
                ...prev.slice(1, prev.length),
              ]);
            }}
          />
          {rxNumbers.map((_, i) => {
            if (i === 0) return null;
            return (
              <AdditionalRxField
                key={rxNumbers[i].id}
                value={rxNumbers[i].value}
                errorText={rxNumbers[i].errorText}
                setValue={(text) => {
                  const errorText = isNotNumeric(text)
                    ? strings.rxNumberNotANumber
                    : "";
                  setRxNumbers((prev) => [
                    ...prev.slice(0, i),
                    {value: text, id: prev[i].id, errorText},
                    ...prev.slice(i + 1, prev.length),
                  ]);
                }}
                onRemove={() =>
                  setRxNumbers((prev) =>
                    prev.slice(0, i).concat(prev.slice(i + 1, prev.length)),
                  )
                }
              />
            );
          })}
          <Button
            className={styles.minimalButton}
            variant="minimal"
            disabled={rxNumbers[0].value === ""}
            onClick={() => {
              setRxNumbers((prev) => [
                ...prev,
                {value: "", id: uuidv4(), errorText: ""},
              ]);
            }}
          >
            {strings.enterAnother}
          </Button>
          <Button
            disabled={
              !firstName ||
              !lastName ||
              !dateOfBirth ||
              rxNumbers.some((r) => r.value === "" || isNotNumeric(r.value))
            }
            loading={isLoading}
            onClick={() => setShowConfirmRefillRequest(true)}
          >
            {strings.requestRefill}
          </Button>
        </div>
        <ConfirmRefillRequest
          visible={showConfirmRefillRequest}
          med={rxNumbers.map((num) => ({name: `Rx ${num.value}`}))}
          onClickCancel={() => setShowConfirmRefillRequest(false)}
          onPickDate={(dateChosen) => setDate(dateChosen)}
          onPickTime={(timeChosen) => setTime(timeChosen)}
          onComment={(commentChosen) => setComment(commentChosen)}
          datePicked={date}
          timePicked={time}
          comment={comment}
          loading={submitting}
          onClickRequest={() => {
            if (date && time) {
              setSubmitting(true);
              submitRefillRequest({
                firstName,
                lastName,
                dateOfBirth,
                scriptNumbers: rxNumbers.map((val) => Number(val.value)),
                pickupDateTime: parse(time, "HH:mm", date).toISOString(),
                pharmacyId: pharmacy?.id ?? 0,
                comment,
              })
                .then((res) => {
                  setShowSuccessModal(true);
                  setResponse(res);

                  setRxNumbers([{value: "", id: uuidv4(), errorText: ""}]);
                  setShowConfirmRefillRequest(false);
                  setDate(undefined);
                  setTime(undefined);
                  setComment("");
                })
                .catch(() => {
                  alert({
                    title: stringsCommon.whoops,
                    message: strings.errorBody(rxNumbers.length),
                  });
                })
                .finally(() => setSubmitting(false));
            }
          }}
        />
        <RegisterSidebar />
      </div>
      <RefillPlacedModal
        visible={showSuccessModal}
        onClickClose={() => {
          setShowSuccessModal(false);
          setResponse([]);
        }}
        refillResponse={response}
        medicationList={response}
      />
    </div>
  );
}
